import React from 'react';
import logo from '../../assets/ruraylogotwo.png';
import { useTranslation } from 'react-i18next';
import { ArrowUp, MessageCircle, Mail, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SITE_CONFIG } from '../../config';

interface FooterProps {
  onOpenPrivacy: () => void;
}

const PRODUCT_LINKS: { labelKey: string; target: string; isAnchor: boolean }[] = [
  { labelKey: 'footer.link_how_it_works', target: '#como-funciona', isAnchor: true },
  { labelKey: 'footer.link_features', target: '#features', isAnchor: true },
  { labelKey: 'footer.integrations', target: '#integraciones', isAnchor: true },
  { labelKey: 'footer.link_pricing', target: '#pricing', isAnchor: true },
  { labelKey: 'footer.link_faq', target: '#faq', isAnchor: true },
];

const Footer: React.FC<FooterProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleAnchorClick = (target: string) => {
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll after render
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(target.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 400);
    } else {
      const element = document.getElementById(target.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer>
      <div className="container">
        {/* ── Grid principal ── */}
        <div className="footer-grid">
          {/* Marca */}
          <div className="footer-brand">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ display: 'inline-block', textDecoration: 'none' }}>
              <img src={logo} alt="Ruray Digital" className="footer-logo" />
            </a>
            <p className="footer-desc">{t('footer.desc')}</p>
            <div className="footer-location">
              <MapPin size={15} /> {t('footer.location')}
            </div>
            <div className="footer-social">
              <a href={`https://wa.me/${SITE_CONFIG.whatsapp.number}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" title="WhatsApp">
                <MessageCircle size={18} />
              </a>
              <a href={`mailto:${SITE_CONFIG.email}`} aria-label="Email" title="Email">
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Producto */}
          <div className="footer-col">
            <h4>{t('footer.product_title')}</h4>
            <ul>
              {PRODUCT_LINKS.map((l) => (
                <li key={l.target}>
                  <a href={l.target} onClick={(e) => { e.preventDefault(); handleAnchorClick(l.target); }}>{t(l.labelKey)}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Empresa */}
          <div className="footer-col">
            <h4>{t('footer.company')}</h4>
            <ul>
              <li><a href="/nosotros" onClick={(e) => { e.preventDefault(); navigate('/nosotros'); }}>{t('navbar.about')}</a></li>
              <li><a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }}>Blog</a></li>
              <li><a href="/contacto" onClick={(e) => { e.preventDefault(); navigate('/contacto'); }}>{t('footer.contact')}</a></li>
            </ul>
          </div>

          {/* Legal y Contacto */}
          <div className="footer-col">
            <h4>{t('footer.contact')}</h4>
            <ul>
              <li><a href={`https://wa.me/${SITE_CONFIG.whatsapp.number}`} target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
              <li><a href={`mailto:${SITE_CONFIG.email}`}>{SITE_CONFIG.email}</a></li>
            </ul>
          </div>
        </div>

        {/* ── Barra inferior ── */}
        <div className="footer-bottom">
          <p>{t('footer.rights')}</p>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
            <a href="/politica-privacidad" onClick={(e) => { e.preventDefault(); navigate('/politica-privacidad'); }} className="footer-linkbtn">{t('footer.privacy')}</a>
            <span className="footer-dot" aria-hidden>·</span>
            <a href="/terminos-condiciones" onClick={(e) => { e.preventDefault(); navigate('/terminos-condiciones'); }} className="footer-linkbtn">{t('footer.terms')}</a>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="footer-top-btn"
              title="Volver al inicio"
              aria-label="Volver al inicio"
            >
              <ArrowUp size={18} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
